import React, { useState } from 'react';
import { captureAuthSession, isAuthSessionCurrent } from '@/lib/authSessionGuard';
import Modal from '@/components/modal/Modal';
import { Col, Row, getItemWithExpiration, setItemWithExpiration } from '@/lib/utils';
import { MediumText, RegularText } from '@/lib/Text';
import { colors } from '@/lib/colors';
import { usePatchCenterVatPolicy } from '@/features/centerVatPolicy';
import { VatPolicy } from '@/features/centerVatPolicy';
import { useQueryClient } from '@tanstack/react-query';
import { overlay } from 'overlay-kit';

type Props = {
    onClose: () => void;
};

const ModalVatPolicySetting = ({ onClose }: Props) => {
    const authObject = getItemWithExpiration('authObject');
    const queryClient = useQueryClient();
    const { mutateAsync: patchVatPolicy } = usePatchCenterVatPolicy();

    const [vatPolicy, setVatPolicy] = useState<VatPolicy>(authObject?.vatPolicy ?? 'inclusive');
    const [vatRate, setVatRate] = useState<string>(String(authObject?.vatRate ?? 10));
    const [isExampleOpen, setIsExampleOpen] = useState(false);

    const hasChanged = vatPolicy !== (authObject?.vatPolicy ?? 'inclusive') || vatRate !== String(authObject?.vatRate ?? 10);

    const handleApply = () => {
        overlay.open(({ unmount }) => (
            <ConfirmModal
                onClose={unmount}
                onConfirm={async () => {
                    // 늦은 응답이 다른 세션 저장소를 덮지 않게 요청 전에 세션 표식을 잡는다.
                    const authSession = captureAuthSession(authObject);
                    try {
                        await patchVatPolicy({
                            centerId: authObject.centerId,
                            body: { vatPolicy, vatRate: Number(vatRate) },
                        });

                        if (!isAuthSessionCurrent(authSession)) return;
                        const newAuthObject = { ...authObject, vatPolicy, vatRate: Number(vatRate) };
                        setItemWithExpiration('authObject', newAuthObject, 12);

                        queryClient.invalidateQueries({ queryKey: ['getCenterSalesSettlementCalendarQuery'] });
                        queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlement'] });
                        queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlementForMembershipQuery'] });
                        queryClient.invalidateQueries({ queryKey: ['getSalaryCalcsQuery'] });

                        onClose();
                    } catch (error) {
                        console.error(error);
                    }
                }}
            />
        ));
    };

    return (
        <Modal onClose={onClose} whiteViewWidth={495} borderRadius={20} isCloseButton={false} isWhiteOverFlowScroll={false}>
            <div className="px-[33px] pt-[28px] pb-[24px]">
                <MediumText fontSize={22} lineHeight={38} color="#3D4244" letterSpacing={-0.5}>
                    부가세 정산 설정
                </MediumText>

                <Col className="flex flex-col mt-[24px] gap-[12px]">
                    {/* 부가세 포함 */}
                    <Row
                        className="flex items-center cursor-pointer"
                        onClick={() => setVatPolicy('inclusive')}
                    >
                        <div
                            className={`w-[22px] h-[22px] rounded-full border-[2px] border-solid flex items-center justify-center mr-[10px] ${
                                vatPolicy === 'inclusive' ? 'border-primary' : 'border-[#D9D9D9]'
                            }`}
                        >
                            {vatPolicy === 'inclusive' && <div className="w-[12px] h-[12px] rounded-full bg-primary" />}
                        </div>
                        <RegularText fontSize={18} lineHeight={28} color="#3D4244">
                            부가세 포함 정산 (기본)
                        </RegularText>
                    </Row>

                    {/* 부가세 미포함 */}
                    <Row
                        className="flex items-center cursor-pointer"
                        onClick={() => setVatPolicy('exclusive')}
                    >
                        <div
                            className={`w-[22px] h-[22px] rounded-full border-[2px] border-solid flex items-center justify-center mr-[10px] ${
                                vatPolicy === 'exclusive' ? 'border-primary' : 'border-[#D9D9D9]'
                            }`}
                        >
                            {vatPolicy === 'exclusive' && <div className="w-[12px] h-[12px] rounded-full bg-primary" />}
                        </div>
                        <RegularText fontSize={18} lineHeight={28} color="#3D4244">
                            부가세 미포함 정산
                        </RegularText>
                    </Row>

                    {/* 세율 입력 */}
                    <Row className="flex items-center ml-[32px] mt-[4px]">
                            <RegularText fontSize={16} lineHeight={24} color="#6B7280" className="mr-[10px]">
                                부가세율
                            </RegularText>
                            <div className="flex items-center border border-solid border-[#D9D9D9] rounded-[10px] px-[14px] h-[44px] w-[100px] focus-within:border-primary transition-colors">
                                <input
                                    type="text"
                                    value={vatRate}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^0-9]/g, '');
                                        if (Number(val) <= 100) {
                                            setVatRate(val);
                                        }
                                    }}
                                    className="outline-none w-full text-[18px] text-[#3D4244]"
                                    maxLength={3}
                                />
                                <RegularText fontSize={18} color="#6B7280" className="ml-[4px]">
                                    %
                                </RegularText>
                            </div>
                        </Row>

                        {/* Layer 1: 개념 설명 + 적용 범위 */}
                        <div className="ml-[32px] mt-[16px] bg-[#F9FAFB] rounded-[16px] px-[18px] py-[16px]">
                            <MediumText fontSize={14} lineHeight={22} color="#3D4244">
                                부가세 미포함 정산이란?
                            </MediumText>
                            <div className="mt-[6px]">
                                <RegularText fontSize={14} lineHeight={22} color="#3D4244">
                                    {'강사 급여를 정산할 때 수강권 매출에 포함되어있는\n부가세를 제외한 실매출을 기준으로 정산합니다.'}
                                </RegularText>
                            </div>

                            <div className="mt-[12px]">
                                <MediumText fontSize={13} lineHeight={20} color="#6B7280">
                                    적용 범위
                                </MediumText>
                                <div className="mt-[4px] flex flex-col gap-[2px]">
                                    <RegularText fontSize={13} lineHeight={20} color="#6B7280">
                                        {'· 급여 계산 — 매출액, 매출인센티브액, 수업료, 최종 급여액'}
                                    </RegularText>
                                    <RegularText fontSize={13} lineHeight={20} color="#6B7280">
                                        {'· 수업료 정산 — 회당가격, 수업 정산액, 매출구간 테이블'}
                                    </RegularText>
                                </div>
                            </div>

                            {/* 구분선 + 토글 */}
                            <div className="mt-[12px] pt-[12px] border-t border-dashed border-[#E5E7EB]">
                                <div
                                    className="flex items-center cursor-pointer"
                                    onClick={() => setIsExampleOpen((prev) => !prev)}
                                >
                                    <RegularText fontSize={14} lineHeight={22} color="#3D73DD">
                                        {isExampleOpen ? '▾' : '▸'} 계산 방식 예시 보기
                                    </RegularText>
                                </div>

                                {/* Layer 2: 계산 예시 */}
                                {isExampleOpen && (
                                    <div className="mt-[10px] bg-white rounded-[12px] p-[14px]">
                                        <RegularText fontSize={13} lineHeight={20} color="#6B7280">
                                            {'수강권 매출 100,000원 · VAT 10% · 정산비율 50% 일 때'}
                                        </RegularText>

                                        <div className="mt-[10px]">
                                            <MediumText fontSize={13} lineHeight={20} color="#3D73DD">
                                                부가세 미포함 정산
                                            </MediumText>
                                            <div className="mt-[4px] flex flex-col gap-[2px]">
                                                <RegularText fontSize={14} lineHeight={22} color="#3D4244">
                                                    {'① 실매출 = 100,000 ÷ 1.1 = '}
                                                    <MediumText fontSize={14} lineHeight={22} color="#3D4244">
                                                        90,909원
                                                    </MediumText>
                                                </RegularText>
                                                <RegularText fontSize={14} lineHeight={22} color="#3D4244">
                                                    {'② 정산액 = 90,909 × 50% = '}
                                                    <MediumText fontSize={14} lineHeight={22} color="#3D73DD">
                                                        45,454원
                                                    </MediumText>
                                                </RegularText>
                                            </div>
                                        </div>

                                    </div>
                                )}
                            </div>
                        </div>
                </Col>

                {/* 버튼 */}
                <Row className="gap-[12px] justify-end mt-[40px]">
                    <div
                        className="w-[110px] h-[41px] bg-[#F4F5F7] rounded-[12px] flex items-center justify-center cursor-pointer"
                        onClick={onClose}
                    >
                        <RegularText fontSize={16} color="#3D4244">
                            취소
                        </RegularText>
                    </div>
                    <div
                        className="w-[110px] h-[41px] rounded-[12px] flex items-center justify-center bg-primary cursor-pointer"
                        onClick={handleApply}
                    >
                        <RegularText fontSize={16} color="#FFFFFF">
                            적용
                        </RegularText>
                    </div>
                </Row>
            </div>
        </Modal>
    );
};

export default ModalVatPolicySetting;

/** 정책 변경 확인 모달 */
const ConfirmModal = ({ onClose, onConfirm }: { onClose: () => void; onConfirm: () => void }) => {
    return (
        <Modal
            onClose={onClose}
            whiteViewWidth={495}
            whiteViewHeight={260}
            borderRadius={20}
            isCloseButton={false}
            isWhiteOverFlowScroll={false}
        >
            <div className="px-[33px] pt-[17px] pb-[21px]">
                <MediumText fontSize={22} lineHeight={38} color="#3D4244" letterSpacing={-0.5}>
                    부가세 정책을 변경할까요?
                </MediumText>
                <Col className="flex flex-col mt-[20px]">
                    <RegularText fontSize={18} lineHeight={30} color="#3D4244">
                        정책을 변경하면 기존 정산 내역도{'\n'}새 정책으로 재계산됩니다.
                    </RegularText>
                </Col>
                <Row className="gap-[12px] justify-end mt-[60px]">
                    <div
                        className="w-[110px] h-[41px] bg-[#F4F5F7] rounded-[12px] flex items-center justify-center cursor-pointer"
                        onClick={onClose}
                    >
                        <RegularText fontSize={16} color="#3D4244">
                            취소
                        </RegularText>
                    </div>
                    <div
                        className="w-[110px] h-[41px] bg-[#3D73DD] rounded-[12px] flex items-center justify-center cursor-pointer"
                        onClick={() => {
                            onClose();
                            onConfirm();
                        }}
                    >
                        <RegularText fontSize={16} color="#FFFFFF">
                            변경하기
                        </RegularText>
                    </div>
                </Row>
            </div>
        </Modal>
    );
};
